import { SpaceDetail } from "@/components/spaces/space-detail/index.tsx";
import { m } from "@/paraglide/messages.js";
import { spaceArtifactsQueryOptions } from "@/services/artifacts-service";
import {
	spaceFolderQueryOptions,
	spaceQueryOptions,
} from "@/services/spaces-service.ts";
import { createEntityErrorBoundary } from "@/utils/entity-error-boundary.tsx";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { pageTitle } from "@/utils/page-title.ts";
import { RoutePending } from "@/components/custom/route-pending.tsx";

const spaceDetailSearchSchema = z.object({
	code: z.string().optional(),
	state: z.string().optional(),
	path: z.string().optional().catch(undefined),
});

export const Route = createFileRoute(
	"/_authenticated/dashboard/spaces/$spaceId",
)({
	validateSearch: spaceDetailSearchSchema,
	loader: async ({ params, context: { queryClient } }) => {
		// Validate here (not in params.parse) so a malformed id throws inside the
		// loader, where the route's errorComponent catches it — param-parse throws
		// happen during matching and escape to the global boundary instead.
		z.uuid().parse(params.spaceId);
		// Feeds both the file listing and the sidebar navigator's top level —
		// they are the same query for the same prefix.
		queryClient.prefetchQuery(spaceFolderQueryOptions(params.spaceId, ""));
		queryClient.prefetchQuery(spaceArtifactsQueryOptions(params.spaceId));
		const space = await queryClient.ensureQueryData(
			spaceQueryOptions(params.spaceId),
		);

		return { space };
	},
	errorComponent: createEntityErrorBoundary({
		title: m.space_not_found_title,
		description: m.space_not_found_description,
		backLabel: m.space_not_found_back,
		backTo: "/dashboard/spaces",
	}),
	head: ({ loaderData }) => ({
		meta: [
			{ title: pageTitle(loaderData?.space.name ?? m.space_detail_title()) },
			{
				name: "description",
				content: m.space_detail_subtitle(),
			},
		],
	}),
	component: RouteComponent,
	pendingComponent: RoutePending,
});

function RouteComponent() {
	return <SpaceDetail />;
}
